class DividendService {

    lastError;
    lastResponseCode;
    isins = [];
    currentRequest = {};
    isSandboxMode = false;

    constructor(axios, t, isSandboxMode) {
        this.axios = axios
        this.t = t
        this.isSandboxMode = isSandboxMode
    }

    async getDividends(opt) {
        let url = '/api/portfolio'
        if(!opt.portfolioId) {
            url += '/dividends'
        } else {
            url += '/'+opt.portfolioId+'/dividends'
        }

        return await this.axios
            .get(url, {
                params: {
                    period: opt.interval || 'fiveyears',
                    context: this.isSandboxMode ? 'virtual' : 'default' 
                },
            })
            .then((res) => {
                this.lastResponseCode = res.status
                return res.data
            })
            .catch((error) => {
                return this.handleError(error);
            })
    }

    async getMonthlyDividends(portfolioId, year, interval = 'fiveyears') {
        let url = '/api/portfolio/dividend/sum'
        if (portfolioId) {
            url += `/${portfolioId}`
        }
        url += `?year=${year}`

        return await this
            .axios
            .get(url, {
                params: {
                    period: interval,
                    context: this.isSandboxMode ? 'virtual' : 'default' 
                },
            })
            .then((res) => {
                this.lastResponseCode = res.status
                return res.data
            })
            .catch((error) => {
                return this.handleError(error);
            })
    }

    async getUnbookedDividends(opt) {
        let url = '/api/portfolio'
        if(!opt.portfolioId) {
            url += '/dividends'
        } else {
            url += '/'+opt.portfolioId+'/dividends/unbooked'
        }

        return await this.axios
            .get(url)
            .then((res) => {
                this.lastResponseCode = res.status
                return res.data
            })
            .catch((error) => {
                return this.handleError(error);
            })
    }

    async save(postData) {
        const requestData = {
            brokerId: postData.broker,
            isin: postData.isin,
            brokerTransactionId: postData.brokerTransactionId,
            positionId: postData.positionId,
            cashflowTypeId: postData.cashflowType,
            qty: postData.qty,
            currency: postData.currency,
            price: postData.price,
            payout: postData.payout,
            fees: postData.fees,
            taxes: postData.taxes,
            payoutDate: postData.payoutDate,
            taxesCurrency: postData.taxesCurrency,
            costs: postData.costs,
            costsCurrency: postData.costsCurrency,
            name: postData.name
        }

        let url = '/api/portfolio/'+postData.portfolioId+'/dividend'
        let method = "post"

        if(postData.dividendId) {
            url = url+'/'+postData.dividendId
            method = "put"
        }

        await this
            .axios({
                method: method,
                url: url,
                data: requestData
            })
            .then((res) => {
                this.lastResponseCode = res.status
                return true
            })
            .catch((res) => this.handleError(res));
    }

    async updateItemField({ portfolioId, dividendId, data }) {
        let url = '/api/portfolio/' + portfolioId + '/dividend/' + dividendId
        let method = "put"
        await this
            .axios({
                method: method,
                url: url,
                data: data
            })
            .then((res) => {
                this.lastResponseCode = res.status
                return true
            })
            .catch((res) => this.handleError(res));
    }

    async bookDividends(portfolioId, dividends) {
        const requestData = dividends.map(dividend => ({
            name: dividend.name,
            isin: dividend.isin,
            qty: dividend.qty,
            currency: dividend.currency,
            price: dividend.price,
            payout: dividend.payout,
            payoutDate: dividend.payoutDate,
            taxes: dividend.taxes,
            fees: dividend.costs,
            cashflowTypeId: dividend.cashflowTypeId,
            positionId: dividend.positionId,
        }))

        let url = `/api/portfolio/${portfolioId}/dividend/bulk`
        let method = "post"

        await this
            .axios({
                method: method,
                url: url,
                data: requestData
            })
            .then((res) => {
                this.lastResponseCode = res.status
                return true
            })
            .catch((res) => this.handleError(res));
    }

    async getShareDividends(isin) {
        if (!isin) throw new Error(this.t('service.dividend.missingIsin'));

        let url = '/api/dividends/share/' + isin

        return await this.axios
            .get(url)
            .then((res) => {
                this.lastResponseCode = res.status;
                return res.data
            })
            .catch((error) => {
                return this.handleError(error);
            })
    }

    async deleteAll(dividendIds) {
        let  url = '/api/dividend/massDelete'

        return await this
            .axios
            .post(url, { ids: dividendIds })
            .then((res) => {
                this.lastResponseCode = res.status
                return res.data
            })
            .catch((error) => {
                return this.handleError(error);
            })
    }

    async deleteDividend(portfolioId, dividendId) {
        if (!dividendId) return this.t('service.dividend.missingDividendId');

        let url = `/api/portfolio/${portfolioId}/dividend/${dividendId}`

        return await this.axios
            .delete(url)
            .then((res) => {
                this.lastResponseCode = res.status
                return res.data
            })
            .catch((error) => {
                return this.handleError(error);
            })
    }

    fetchIsins(res) {
        for (let i = 0; i < res.data.length; i++) {
            let isin = res.data[i].isin
            this.isins.push(isin)
        }
        return this.isins
    }

    async getCashflowTypes(assetGroupKey) {
        if (!assetGroupKey) throw new Error(this.t('service.dividend.missingAssetGroupKey'));

        const url = `/api/cashflowType/${assetGroupKey}`

        if (this.currentRequest.getCashflowTypes) {
            await this.currentRequest.getCashflowTypes.cancel()
        }
        const axiosSource = this.axios.CancelToken.source()
        this.currentRequest.getCashflowTypes = { cancel: axiosSource.cancel }

        return await this.axios
            .get(url, { cancelToken: axiosSource.token })
            .then((res) => {
                this.lastResponseCode = res.status
                return res.data
            })
            .catch((error) => {
                if (this.axios.isCancel(error)) {
                    return null
                }
                return this.handleError(error);
            })
    }

    async createMonthlyCashflows(postData) {
        const requestData = {
            brokerId: postData.broker,
            isin: postData.isin,
            positionId: postData.positionId,
            cashflowTypeId: postData.cashflowType,
            qty: postData.qty,
            currency: postData.currency,
            price: postData.price,
            payout: postData.payout,
            fees: postData.fees,
            taxes: postData.taxes,
            payoutDate: postData.payoutDate,
            taxesCurrency: postData.taxesCurrency,
            costs: postData.costs,
            costsCurrency: postData.costsCurrency,
            name: postData.name
        }

        const url = '/api/portfolio/'+ postData.portfolioId + '/dividend/year'
        const method = "post"

        await this
            .axios({
                method: method,
                url: url,
                data: requestData
            })
            .then((res) => {
                this.lastResponseCode = res.status
                return true
            })
            .catch((res) => this.handleError(res));
    }

    handleError(e) {
        this.lastResponseCode = e.response?.status || e.response?.data?.code || e.code
        this.lastError =  e.response?.data?.message || e.response?.statusText || e.message

        throw new Error(this.t('service.dividend.generalError', { error: this.lastError }));
    }
}

export default DividendService

